import {
  JupiterTheme,
  MockHook,
  NAVBAR_HEIGHT,
  J1Searchbar,
  SortablePaginationQueryResult,
  QuestionResult,
} from '@jupiterone/jupiter-web-toolkit';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import useSearchAnywhereLogic, {
  SearchAnywhereContainerComponentProps,
} from './useSearchAnywhereContainerLogic';
import clsx from 'clsx';
import { X as XIcon } from 'react-feather';
import ContainerInfo from './ContainerInfo';

export const HEIGHT_OF_BANNER = 96;
const FULL_HEIGHT = `calc(100vh - ${NAVBAR_HEIGHT}px + 3px)`;
const useStyles = makeStyles((theme: JupiterTheme) => {
  return createStyles({
    rootSearchAnywhereContainerInactive: {
      top: `calc(${NAVBAR_HEIGHT}px - 3px)`,
      left: 0,
      position: 'absolute',
      overflow: 'hidden',
      maxHeight: 0,
      width: '100%',
      display: 'none',
    },
    rootSearchAnywhereContainerActive: {
      height: FULL_HEIGHT,
      display: 'block',
      maxHeight: FULL_HEIGHT,
      width: '100%',
    },
    searchAnywhereMain: {
      background: theme.palette.gradient.queryAnywhere,
      boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.25)',
      height: HEIGHT_OF_BANNER,
      zIndex: 3,
      display: 'flex',
      flexDirection: 'row',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
    },
    searchAnywhereInternal: {
      display: 'flex',
      flexDirection: 'column',
    },
    searchResults: {
      width: '80%',
      zIndex: -1,
      minHeight: 500 + theme.spacing(1),
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: theme.spacing(0, 2, 2, 2),
      background: 'linear-gradient(265.91deg, #53D6BB 0%, #496EDC 93.32%)',
      boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.25)',
      borderRadius: '0px 0px 6px 6px',
    },
    rightSide: {
      display: 'flex',
      height: HEIGHT_OF_BANNER,
      boxShadow: '0px 8px 8px -8px rgba(0, 0, 0, 0.25)',
      width: '10%',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    xIcon: {
      marginTop: theme.spacing(2),
      marginBottom: 'auto',
      marginLeft: 'auto',
      marginRight: 20,
      cursor: 'pointer',
      color: theme.palette.common.white,
      borderRadius: '100%',
      strokeWidth: '1.5px',
      height: 25,
      width: 25,
      border: `1px solid ${theme.palette.common.white}`,
      padding: theme.spacing(0.5),
      '&:hover': {
        backgroundColor: theme.palette.common.white,
        color: '#53D5BB',
      },
    },
    searchContainer: {
      width: 740,
      height: '100%',
    },
    backdrop: {
      height: FULL_HEIGHT,
      width: '100%',
      background: 'rgba(0, 0, 0, 0.3)',
    },
    clickCatcher: {
      height: '100%',
      width: '100%',
    },
    resultPaper: {
      width: '100%',
      height: '100%',
    },
    queryPaper: {
      boxShadow: '0px 3px 5px -2px rgba(0, 0, 0, 0.2)',
    },
    questionPaper: {
      background: theme.palette.background.paper,
    },
    resultsOuterContainer: {
      display: 'flex',
      flexDirection: 'row',
      zIndex: 1,
      position: 'absolute',
      top: HEIGHT_OF_BANNER,
      left: 0,
      right: 0,
      bottom: theme.spacing(2),
      overflow: 'auto',
    },
    dropShadowEmpty: {
      boxShadow: '0px 8px 8px -8px rgba(0, 0, 0, 0.25)',
      width: '10%',
      height: '100%',
    },
    dropShadowEmptyInactive: {
      boxShadow: 'none',
    },
    instructions: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      paddingBottom: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    centerSearchContainer: {
      display: 'flex',
      flexDirection: 'row',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingLeft: '10%',
      [theme.breakpoints.down('md')]: {
        paddingLeft: '0',
      },
    },
    disabledPadding: {
      padding: 0,
    },
    sideClickCatcher: {
      width: '10%',
      height: '100%',
    },
    resultsHeight: {
      height: `calc(100% - ${theme.spacing(0.5)}px)`,
      minHeight: `calc(100% - ${theme.spacing(0.5)}px)`,
    },
  });
});

export const SearchAnywhereContainerComponent = ({
  searchAnywhereOpen,
  searches,
  searchText,
  handleClose,
  handleNewSearch,
  handleClearSearchList,
  handleAddQuestion,
  handleNoResultBackgroundClick,
}: SearchAnywhereContainerComponentProps) => {
  const classes = useStyles();

  return (
    <div
      className={clsx(
        classes.rootSearchAnywhereContainerInactive,
        searchAnywhereOpen && classes.rootSearchAnywhereContainerActive
      )}
      id="targetForWidthofLinearGradient"
    >
      <div className={classes.backdrop}>
        <div className={classes.searchAnywhereInternal}>
          <div
            className={clsx(
              classes.searchAnywhereMain,
              searches.state &&
                searches.state.length > 0 &&
                classes.dropShadowEmptyInactive
            )}
          >
            <div
              className={clsx(
                classes.centerSearchContainer,
                searches.state &&
                  searches.state.length > 0 &&
                  classes.disabledPadding
              )}
            >
              <div className={classes.instructions}>
                <ContainerInfo handleSearchAnywhereClose={handleClose} />
              </div>
              <div className={classes.searchContainer}>
                <J1Searchbar
                  toggleQuestionList={handleClose}
                  hasResults={false}
                  lastEditableQuery={searchText}
                  onAddQuestion={handleAddQuestion}
                  onSearch={handleNewSearch}
                  onClearAllResults={handleClearSearchList}
                  showGradientBackground={true}
                />
              </div>
            </div>
            <div className={classes.rightSide}>
              <XIcon className={classes.xIcon} onClick={handleClose} />
            </div>
          </div>
          <div
            className={classes.resultsOuterContainer}
            onClick={handleNoResultBackgroundClick}
          >
            {searches.state && searches.state.length > 0 && (
              <>
                <div
                  onClick={handleClose}
                  className={classes.sideClickCatcher}
                />
                <div className={classes.searchResults}>
                  {searches.state && searches.state[0] && (
                    <div className={classes.resultsHeight}>
                      {searches.state[0].type === 'query' ? (
                        <SortablePaginationQueryResult
                          key={searches.state[0].runTimestamp}
                          query={searches.state[0].query}
                          className={clsx(
                            classes.resultPaper,
                            classes.queryPaper
                          )}
                          onRemoveResult={() => searches.deleteAt(0)}
                          savable
                        />
                      ) : (
                        <QuestionResult
                          key={searches.state[0].runTimestamp}
                          question={searches.state[0].question}
                          onRemoveResult={() => searches.deleteAt(0)}
                          onUpdateResult={(q) =>
                            searches.upsertAt(
                              {
                                question: q,
                                type: 'question',
                                runTimestamp: new Date().getTime(),
                              },
                              0
                            )
                          }
                          className={clsx(
                            classes.resultPaper,
                            classes.questionPaper
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div
                  onClick={handleClose}
                  className={classes.sideClickCatcher}
                />
              </>
            )}
          </div>
        </div>
        <div onClick={handleClose} className={classes.clickCatcher}></div>
      </div>
    </div>
  );
};
export default MockHook.withHookHoc(
  SearchAnywhereContainerComponent,
  useSearchAnywhereLogic
);
